import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authApi } from '@services/auth.service'
import { useAuthStore } from '@hooks/useAuthStore'
import { useToast } from '@hooks/use-toast'
import { AuthLayout } from '@components/layout/AuthLayout'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { cn } from '@lib/utils'
import { Eye, EyeOff, Loader2, Mail, Lock, AlertCircle } from 'lucide-react'

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const login = useAuthStore((state) => state.login)

  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const DEMO_PASSWORD = 'Demo1234!@#$'
  const DEMO_ROLES = [
    { label: 'Admin', email: 'admin@hms.com', password: DEMO_PASSWORD },
    { label: 'Doctor', email: 'doctor@hms.com', password: DEMO_PASSWORD },
    { label: 'Enfermería', email: 'nurse@hms.com', password: DEMO_PASSWORD },
    { label: 'Recepción', email: 'reception@hms.com', password: DEMO_PASSWORD },
    { label: 'Farmacia', email: 'pharmacy@hms.com', password: DEMO_PASSWORD },
    { label: 'Facturación', email: 'billing@hms.com', password: DEMO_PASSWORD },
  ]
  const [activeRole, setActiveRole] = useState(0)
  const [formData, setFormData] = useState({
    email: DEMO_ROLES[0].email,
    password: DEMO_ROLES[0].password,
  })

  useEffect(() => {
    if (searchParams.get('reason') === 'session-expired') {
      toast({
        title: 'Sesión expirada',
        description: 'Tu sesión se perdió o venció. Inicia sesión de nuevo para continuar.',
        variant: 'destructive',
      })
    }
  }, [searchParams, toast])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setErrorMessage('')

    try {
      const response = await authApi.loginJson({
        email: formData.email,
        password: formData.password,
      })

      login(response)

      toast({
        title: t('auth.welcome_back'),
        description: `Sesión iniciada como ${response.user.first_name} ${response.user.last_name}`,
      })

      navigate(searchParams.get('next') || '/agenda')
    } catch (error) {
      setErrorMessage(t('auth.login_error_desc'))
    } finally {
      setIsLoading(false)
    }
  }

  const hasError = Boolean(errorMessage)

  return (
    <AuthLayout>
      <p className="auth-eyebrow">Portal de acceso clínico</p>
      <h2 className="auth-title">Bienvenido de vuelta</h2>
      <p className="auth-subtitle">{t('auth.portal_subtitle')}</p>

      {/* Demo role selector — pre-fills credentials for each staff role */}
      <div className="auth-roles" role="group" aria-label="Seleccionar rol de demostración">
        {DEMO_ROLES.map((role, i) => (
          <button
            key={role.label}
            type="button"
            aria-pressed={activeRole === i}
            className={cn('auth-role', activeRole === i && 'is-active')}
            onClick={() => {
              setActiveRole(i)
              setFormData({ email: role.email, password: role.password })
              setErrorMessage('')
            }}
          >
            {role.label}
          </button>
        ))}
      </div>
      <p className="auth-hint" role="note">
        Credenciales demo cargadas automáticamente. Puedes cambiar el rol o editar los campos.
      </p>

      {/* Reserved area for backend authentication errors */}
      {hasError && (
        <div id="login-error" className="auth-error" role="alert" aria-live="assertive">
          <AlertCircle size={18} strokeWidth={2} />
          <span>{errorMessage}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="mt-6 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="username">ID de especialista / Correo</Label>
          <div className="auth-input-wrap">
            <Mail className="auth-input-icon" size={18} strokeWidth={1.75} />
            <Input
              type="email"
              id="username"
              name="email"
              placeholder="nombre@clinica.com"
              required
              autoComplete="username"
              aria-invalid={hasError}
              aria-describedby={hasError ? 'login-error' : undefined}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={cn('h-12 pl-11', hasError && 'border-destructive focus-visible:ring-destructive')}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Contraseña de acceso</Label>
          <div className="auth-input-wrap">
            <Lock className="auth-input-icon" size={18} strokeWidth={1.75} />
            <Input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              placeholder="••••••••"
              required
              autoComplete="current-password"
              aria-invalid={hasError}
              aria-describedby={hasError ? 'login-error' : undefined}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className={cn('h-12 pl-11 pr-12', hasError && 'border-destructive focus-visible:ring-destructive')}
            />
            <button
              type="button"
              className="auth-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? t('auth.hide_password') : t('auth.show_password')}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <div className="flex justify-end">
          <Link to="/forgot-credentials" className="auth-forgot">
            ¿Olvidó sus credenciales?
          </Link>
        </div>

        <Button type="submit" className="auth-submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 size={18} strokeWidth={2} className="animate-spin" />
              Iniciando sesión…
            </>
          ) : (
            'Iniciar sesión'
          )}
        </Button>
      </form>
    </AuthLayout>
  )
}
